'use client'

import * as React from 'react'
import {
  ListIcon,
  SquaresFourIcon,
  CalendarBlankIcon,
  CalendarIcon,
  type Icon,
} from '@phosphor-icons/react'
import { cn } from '@/lib/tailwind'

export type ViewMode = 'list' | 'grid' | 'timeline' | 'calendar'

export interface ViewSwitcherProps {
  value: ViewMode
  onChange: (mode: ViewMode) => void
  options?: ViewMode[]
  className?: string
}

const VIEW_MODE_CONFIG: Record<
  ViewMode,
  {
    icon: Icon
    label: string
  }
> = {
  list: { icon: ListIcon, label: 'List view' },
  grid: { icon: SquaresFourIcon, label: 'Grid view' },
  timeline: { icon: CalendarBlankIcon, label: 'Timeline view' },
  calendar: { icon: CalendarIcon, label: 'Calendar view' },
}

/**
 * ViewSwitcher component allows toggling between different view modes.
 *
 * Design features:
 * - Icon-only buttons for compact display
 * - Visual feedback for selected state via animated clip-path highlight
 * - 44px minimum touch targets on mobile
 * - Grouped button appearance
 */
export function ViewSwitcher({
  value,
  onChange,
  options = ['list', 'grid', 'timeline'],
  className,
}: ViewSwitcherProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const activeButtonRef = React.useRef<HTMLButtonElement>(null)
  const [clipPath, setClipPath] = React.useState<string>(
    'inset(0 100% 0 0 round 8px)',
  )

  React.useEffect(() => {
    const container = containerRef.current
    const activeBtn = activeButtonRef.current
    if (!container || !activeBtn) return

    const containerRect = container.getBoundingClientRect()
    const btnRect = activeBtn.getBoundingClientRect()

    const leftPx = btnRect.left - containerRect.left
    const rightPx = containerRect.right - btnRect.right

    const containerWidth = containerRect.width
    const leftPct = (leftPx / containerWidth) * 100
    const rightPct = (rightPx / containerWidth) * 100

    setClipPath(
      `inset(0 ${rightPct.toFixed(2)}% 0 ${leftPct.toFixed(2)}% round 8px)`,
    )
  }, [value, options])

  return (
    <div
      ref={containerRef}
      className={cn(
        'bg-background relative inline-flex items-center rounded-lg border',
        className,
      )}
      role="group"
      aria-label="View mode selector"
    >
      {/* Animated highlight overlay */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 rounded-lg',
          'transition-[clip-path] duration-[200ms] ease-in-out',
          'motion-reduce:transition-none',
        )}
        style={{ clipPath }}
        aria-hidden="true"
      >
        <div className="bg-foreground/10 absolute inset-0 rounded-lg" />
      </div>

      {options.map((mode, index) => {
        const config = VIEW_MODE_CONFIG[mode]
        const IconComp = config.icon
        const isSelected = value === mode
        const isFirst = index === 0
        const isLast = index === options.length - 1

        return (
          <button
            key={mode}
            ref={isSelected ? activeButtonRef : null}
            onClick={() => onChange(mode)}
            className={cn(
              'relative inline-flex items-center justify-center',
              'min-h-[44px] min-w-[44px] sm:min-h-9 sm:min-w-9',
              'px-3 py-2',
              'transition-colors',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
              isSelected
                ? 'text-foreground'
                : 'text-muted-foreground hover:text-foreground',
              isFirst && 'rounded-l-lg',
              isLast && 'rounded-r-lg',
              !isFirst && 'border-l',
            )}
            aria-label={config.label}
            aria-pressed={isSelected}
            type="button"
          >
            <IconComp
              className="size-5 shrink-0 sm:size-4"
              weight={isSelected ? 'fill' : 'regular'}
              aria-hidden="true"
            />
          </button>
        )
      })}
    </div>
  )
}
